import * as React from "react";
import { Sparkles } from "lucide-react";
import {
  Background,
  BackgroundVariant,
  Controls,
  MarkerType,
  MiniMap,
  type ReactFlowInstance,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
  type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { Button } from "@/components/ui/button";

import { applyDagreLayout } from "./layout";
import {
  CargoNode,
  ContractNode,
  CustomerNode,
  DeliveryNode,
  DocumentNode,
  OrderItemNode,
  OrderNode,
  PaymentNode,
  ProductNode,
  WarehouseNode,
} from "./nodes";
import type {
  CargoNodeData,
  ContractNodeData,
  CustomerNodeData,
  DeliveryNodeData,
  DetailData,
  DocumentNodeData,
  FlowNodeData,
  FlowNodeType,
  OrderItemNodeData,
  OrderNodeData,
  PaymentNodeData,
  ProductNodeData,
  WarehouseNodeData,
} from "./nodes/types";

export interface OrderFlowGraphData {
  order: {
    _id: string;
    orderNumber?: string;
    status?: string;
    totalAmount?: number;
    createdAt?: string | number;
    [key: string]: unknown;
  };
  user?: {
    _id: string;
    userId?: string;
    name?: string | null;
    email?: string | null;
    company?: string | null;
    [key: string]: unknown;
  } | null;
  items: Array<{
    _id: string;
    quantity?: number;
    unitPrice?: number;
    isContracted?: boolean;
    productName?: string;
    cargo?: {
      _id: string;
      ociNumber?: string;
      status?: string;
      availableBags?: number;
      totalBags?: number;
      [key: string]: unknown;
    } | null;
    product?: {
      _id: string;
      name?: string;
      weight?: number;
      score?: number;
      [key: string]: unknown;
    } | null;
    warehouse?: {
      _id: string;
      name?: string;
      state?: string;
      [key: string]: unknown;
    } | null;
    [key: string]: unknown;
  }>;
  payments: Array<{
    _id: string;
    amount?: number;
    status?: string;
    paymentType?: string;
    transactionId?: string;
    [key: string]: unknown;
  }>;
  delivery?: {
    _id: string;
    deliveryMethod?: string;
    trackingNumber?: string;
    trackingUrl?: string;
    estimatedDeliveryDate?: string | number;
    [key: string]: unknown;
  } | null;
  contract?: {
    _id: string;
    contractNumber?: string;
    contractStatus?: string;
    totalAmount?: number;
    [key: string]: unknown;
  } | null;
  documents: Array<{
    _id: string;
    type?: string;
    url?: string;
    [key: string]: unknown;
  }>;
}

export interface SelectedFlowNode {
  id: string;
  type: FlowNodeType;
  title: string;
  data: DetailData;
}

interface FlowGraphProps {
  flow: OrderFlowGraphData;
  onSelectNode: (node: SelectedFlowNode | null) => void;
}

type FlowGraphNode = Node<FlowNodeData, FlowNodeType>;

const nodeTypes: NodeTypes = {
  order: OrderNode,
  customer: CustomerNode,
  orderItem: OrderItemNode,
  cargo: CargoNode,
  product: ProductNode,
  warehouse: WarehouseNode,
  payment: PaymentNode,
  delivery: DeliveryNode,
  contract: ContractNode,
  document: DocumentNode,
} as NodeTypes;

const EDGE_STROKE_COLOR = "var(--muted-foreground)";
const EDGE_MARKER_COLOR = "var(--foreground)";
const MINIMAP_NODE_COLOR = "var(--muted-foreground)";

type FlowEdgeType = "step" | "straight";

function makeNode<TData extends FlowNodeData>(
  id: string,
  type: FlowNodeType,
  data: TData,
): FlowGraphNode {
  return {
    id,
    type,
    data,
    position: { x: 0, y: 0 },
  } as FlowGraphNode;
}

function makeEdge(source: string, target: string, edgeType: FlowEdgeType): Edge {
  return {
    id: `${source}__${target}`,
    source,
    target,
    type: edgeType,
    zIndex: 1,
    interactionWidth: 28,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: EDGE_MARKER_COLOR,
    },
    style: {
      stroke: EDGE_STROKE_COLOR,
      strokeOpacity: 0.4,
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
    },
  };
}

function buildGraph(
  flow: OrderFlowGraphData,
): { nodes: FlowGraphNode[]; edges: Edge[] } {
  const nodes: FlowGraphNode[] = [];
  const edges: Edge[] = [];
  const nodeIds = new Set<string>();
  const edgeIds = new Set<string>();
  const addNode = (node: FlowGraphNode) => {
    if (nodeIds.has(node.id)) return;
    nodeIds.add(node.id);
    nodes.push(node);
  };

  const addEdge = (source: string, target: string, edgeType: FlowEdgeType = "step") => {
    const id = `${source}__${target}`;
    if (edgeIds.has(id)) return;
    edgeIds.add(id);
    edges.push(makeEdge(source, target, edgeType));
  };

  const orderId = `order:${flow.order._id}`;
  const orderNodeData: OrderNodeData = {
    orderNumber: flow.order.orderNumber || "Order",
    status: flow.order.status,
    totalAmount: flow.order.totalAmount,
    createdAt: flow.order.createdAt,
    detailTitle: flow.order.orderNumber || "Order",
    detailData: flow.order,
  };
  addNode(makeNode(orderId, "order", orderNodeData));

  if (flow.user) {
    const customerId = `customer:${flow.user._id}`;
    const customerNodeData: CustomerNodeData = {
      name: flow.user.name,
      company: flow.user.company,
      email: flow.user.email,
      profileUrl: `/logistics/users?userId=${encodeURIComponent(flow.user._id)}`,
      detailTitle: flow.user.company || flow.user.name || "Customer",
      detailData: flow.user,
    };
    addNode(makeNode(customerId, "customer", customerNodeData));
    addEdge(customerId, orderId, "straight");
  }

  flow.items.forEach((item, index) => {
    const itemId = `item:${item._id || index}`;
    const itemNodeData: OrderItemNodeData = {
      productName: item.productName,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      isContracted: item.isContracted,
      detailTitle: item.productName || `Order Item ${index + 1}`,
      detailData: item,
    };
    addNode(makeNode(itemId, "orderItem", itemNodeData));
    addEdge(orderId, itemId);

    if (!item.cargo) return;

    const cargoId = `cargo:${item.cargo._id}`;
    const cargoNodeData: CargoNodeData = {
      ociNumber: item.cargo.ociNumber,
      status: item.cargo.status,
      availableBags: item.cargo.availableBags,
      totalBags: item.cargo.totalBags,
      detailTitle: item.cargo.ociNumber || "Cargo",
      detailData: item.cargo,
    };
    addNode(makeNode(cargoId, "cargo", cargoNodeData));
    addEdge(itemId, cargoId, "straight");

    if (item.product) {
      const productId = `product:${item.product._id}`;
      const productNodeData: ProductNodeData = {
        name: item.product.name,
        weight: item.product.weight,
        score: item.product.score,
        detailTitle: item.product.name ?? "",
        detailData: item.product,
      };
      addNode(makeNode(productId, "product", productNodeData));
      addEdge(cargoId, productId);
    }

    if (item.warehouse) {
      const warehouseId = `warehouse:${item.warehouse._id}`;
      const warehouseNodeData: WarehouseNodeData = {
        name: item.warehouse.name,
        state: item.warehouse.state,
        detailTitle: item.warehouse.name || "Warehouse",
        detailData: item.warehouse,
      };
      addNode(makeNode(warehouseId, "warehouse", warehouseNodeData));
      addEdge(cargoId, warehouseId);
    }
  });

  flow.payments.forEach((payment, index) => {
    const paymentId = `payment:${payment._id || index}`;
    const paymentNodeData: PaymentNodeData = {
      amount: payment.amount,
      status: payment.status,
      paymentType: payment.paymentType,
      transactionId: payment.transactionId,
      detailTitle: `Payment ${index + 1}`,
      detailData: payment,
    };
    addNode(makeNode(paymentId, "payment", paymentNodeData));
    addEdge(orderId, paymentId);
  });

  if (flow.delivery) {
    const deliveryId = `delivery:${flow.delivery._id}`;
    const deliveryNodeData: DeliveryNodeData = {
      deliveryMethod: flow.delivery.deliveryMethod,
      trackingNumber: flow.delivery.trackingNumber,
      trackingUrl: flow.delivery.trackingUrl,
      estimatedDeliveryDate: flow.delivery.estimatedDeliveryDate,
      detailTitle: "Delivery",
      detailData: flow.delivery,
    };
    addNode(makeNode(deliveryId, "delivery", deliveryNodeData));
    addEdge(orderId, deliveryId);
  }

  if (flow.contract) {
    const contractId = `contract:${flow.contract._id}`;
    const contractNodeData: ContractNodeData = {
      contractNumber: flow.contract.contractNumber,
      status: flow.contract.contractStatus,
      totalAmount: flow.contract.totalAmount,
      detailTitle: flow.contract.contractNumber || "Contract",
      detailData: flow.contract,
    };
    addNode(makeNode(contractId, "contract", contractNodeData));
    addEdge(orderId, contractId);
  }

  flow.documents.forEach((document, index) => {
    const documentId = `document:${document._id || index}`;
    const documentNodeData: DocumentNodeData = {
      documentType: document.type,
      url: document.url,
      detailTitle: document.type || `Document ${index + 1}`,
      detailData: document,
    };
    addNode(makeNode(documentId, "document", documentNodeData));
    addEdge(orderId, documentId);
  });

  return applyDagreLayout(nodes, edges);
}

export function FlowGraph({ flow, onSelectNode }: FlowGraphProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowGraphNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const reactFlowRef = React.useRef<ReactFlowInstance<FlowGraphNode, Edge> | null>(null);

  const applyLayout = React.useCallback(
    ({ fitView = false }: { fitView?: boolean } = {}) => {
      const layouted = buildGraph(flow);
      setNodes(layouted.nodes);
      setEdges(layouted.edges);

      if (fitView) {
        requestAnimationFrame(() => {
          reactFlowRef.current?.fitView({
            padding: 0.24,
            duration: 280,
            maxZoom: 1.25,
          });
        });
      }
    },
    [flow, setEdges, setNodes],
  );

  React.useEffect(() => {
    applyLayout({ fitView: true });
  }, [applyLayout]);

  const handleNodeClick = React.useCallback(
    (_event: React.MouseEvent, node: FlowGraphNode) => {
      onSelectNode({
        id: node.id,
        type: node.type || "order",
        title: node.data.detailTitle,
        data: node.data.detailData,
      });
    },
    [onSelectNode],
  );

  const handlePaneClick = React.useCallback(() => {
    onSelectNode(null);
  }, [onSelectNode]);

  return (
    <div className="relative h-full w-full">
      <div className="absolute bottom-4 left-14 z-10 rounded-[10px] border border-border/70 bg-background/95 p-1 shadow-sm backdrop-blur">
        <Button
          type="button"
          size="icon-xs"
          variant="outline"
          className="border-border/70 bg-background/90"
          onClick={() => applyLayout()}
          aria-label="Tidy graph layout"
          title="Tidy graph layout"
        >
          <Sparkles className="size-3.5" />
        </Button>
      </div>
      <ReactFlow<FlowGraphNode, Edge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onInit={(instance) => {
          reactFlowRef.current = instance;
        }}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        minZoom={0.2}
        maxZoom={1.75}
        nodesDraggable
        nodesConnectable={false}
        selectNodesOnDrag={false}
        defaultEdgeOptions={{
          type: "step",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        proOptions={{ hideAttribution: true }}
      >
        <MiniMap
          position="bottom-right"
          pannable
          zoomable
          className="!border !border-border/70 !bg-background/95"
          nodeColor={MINIMAP_NODE_COLOR}
        />
        <Controls position="bottom-left" showInteractive={false} />
        <Background variant={BackgroundVariant.Dots} gap={18} size={1.1} />
      </ReactFlow>
    </div>
  );
}
